import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'next/router';
import {
  Query, compose, graphql, withApollo,
} from 'react-apollo';
import { format } from 'date-fns';
import { isEqual } from 'lodash';

import { getPayloadFromQuoteAndQuery } from '../../../lib/helpers/quote';
import { parseVariables, getTotalCost } from '../../../lib/availability';
import { getProductWithCampsiteType } from '../../../lib/campsiteTypes';
import { dictionaryItem } from '../../../hocs/withDictionary';

import Availability, { AvailabilitySection } from '../../Availability';
import AvailabilityAmend from '../../Amend/AvailabilityAmend';
import AvailabilitySearchQuery from '../../Availability/AvailabilitySearchQuery';
import AvailabilityDetails from '../../AvailabilityDetails';
import { LoadingSpinner } from '../../ui/Loading';
import { Text } from '../../ui';
import { Body } from '../../SiteCard/SiteCard.style';

import GET_AVAILABILITY_DETAILS from '../../AvailabilityDetails/graphql/getAvailabilityDetails.gql';
import GET_CONFIGURATION from '../../../config/graphql/getConfiguration';
import GET_MEMBER_STATUS from '../../../config/graphql/getUser';
import GET_BOOKING_DETAILS from '../../ManageMyBookings/graphql/getCurrentUserBooking.gql';
import isLoggedIn, { isLoggedInMember } from '../../../lib/isLoggedIn';
import { DIALOG_MEMBERSHIP_TYPE } from '../../../config/dialogMembership';
import { getMembershipDialogProps, getDialogType } from '../../../lib/helpers/dialogMembership';
import quoteMembershipTypes, {
  getENumWithValue, newMemberTypes,
} from '../../../config/quoteMembershipTypes';
import { RENEWAL_STATUSES } from '../../../config/membershipStatus';
import MembershipDialog from '../../Dialog/MembershipDialog';
import { updateQuoteCache } from '../../../resolvers/quote';

import withStateMutation from '../../../hocs/withStateMutation';
import UPDATE_QUOTE from '../graphql/updateQuote';
import IbePropTypes from '../../../IbePropTypes';
import FetchPolicy from '../../../constants/FetchPolicy';
import AddToBasket from '../../AddToBasket/AddToBasket';
import { parsePayload } from '../../AvailabilityDetails/AvailabilityDetailsFooter';
import updateRouterQuery from '../../../lib/updateRouterQuery';
import routes from '../../../constants/routes';
import { redirectViaRouterToLoginWithBasketOpen, addToBasketRedirect } from '../../Basket/helpers';
import { checkIfEventIsClosedOnWithinDateRange } from '../../Availability/helpers';
import { getBookingFromData } from '../../ManageMyBookings/helpers';
import { populateQuoteFromBooking } from '../../../lib/helpers/amend';
import { bookingReducedNights } from '../../Amend/helpers';
import { addTiming, startTiming, types } from '../../../lib/timings';
import GET_SITES_CONFIG from '../../../config/graphql/getSitesConfig';

/**
 * TODO: Remove all campsite specific code into seperate component
 */
class QuoteCampsite extends Component {
  static propTypes = {
    booking: PropTypes.shape(IbePropTypes.booking),
    countBookings: PropTypes.number,
    error: PropTypes.shape({}),
    loading: PropTypes.bool,
    quoteLoading: PropTypes.bool,
    onlyExtras: PropTypes.bool.isRequired,
    onResultChange: PropTypes.func.isRequired,
    onSelectEvent: PropTypes.func.isRequired,
    query: PropTypes.shape(IbePropTypes.query),
    quote: PropTypes.shape(IbePropTypes.quote),
    result: PropTypes.shape(IbePropTypes.campsite),
    router: PropTypes.shape(IbePropTypes.router).isRequired,
    toggleBasket: PropTypes.func.isRequired,
    onQueryChange: PropTypes.func.isRequired,
    data: PropTypes.shape({
      configuration: PropTypes.shape(IbePropTypes.configuration),
      loading: false,
    }),
    updateQuote: PropTypes.func.isRequired,
    membershipStatus: PropTypes.shape({
      user: PropTypes.shape(IbePropTypes.user),
    }),
    selectedEvent: PropTypes.shape(IbePropTypes.event),
    client: PropTypes.shape(IbePropTypes.client).isRequired,
    onProductTypeMismatch: PropTypes.func.isRequired,
    sitesConfig: PropTypes.shape(IbePropTypes.sitesConfig).isRequired,
  };

  static defaultProps = {
    booking: null,
    countBookings: null,
    error: null,
    loading: false,
    quoteLoading: false,
    query: {},
    quote: {
      partyMembers: [],
      crossingBookings: [],
      campsiteBookings: [],
      id: '',
      extras: [],
    },
    result: {
      address: {},
    },
    data: {
      configuration: undefined,
    },
    membershipStatus: undefined,
    selectedEvent: null,
  }

  constructor(props) {
    super();
    this.state = {
      addToBasketCallback: null,
      bookingEdited: false,
      continued: false,
      disabledForms: false,
      errors: [],
      eventChecked: false,
      maxOccupancy: null,
      memberPricesAreSelected: true,
      membershipDialog: null,
      isLoadingPayload: true,
    };

    this.basePrices = {};
    this.topRef = React.createRef();
    this.availabilitySearchQueryRef = React.createRef();
  }

  componentDidMount() {
    try {
      localStorage.removeItem('pendingLogin');
    } catch (error) {
      console.error(error);
    }

    this.generatePayloadFromQuoteAndBooking(this.props);

    this.checkAmendsMembershipSelection();
  }

  componentDidUpdate(prevProps) {
    const {
      query, quote, router, booking,
    } = this.props;

    const queryChanged = !isEqual(prevProps.query, query);
    const quoteChanged = !isEqual(prevProps.quote, quote);
    const quoteDeleted = (prevProps.quote?.quoteId && !quote?.quoteId) || (
      prevProps.quote?.bookingReference && !quote?.bookingReference
    );

    if (queryChanged || quoteChanged) {
      const datesChanged = prevProps.query.start !== query.start ||
        prevProps.query.end !== query.end;
      const componentIdChanged = prevProps.query.componentId !== query.componentId ||
        query.bookingId;

      this.setPayload(datesChanged, componentIdChanged, quoteDeleted);
    }
    if (quote?.nonMemberSupplementsCost !== undefined) {
      this.handleCheckMembership(null, quote);
    }

    if (prevProps?.router?.query.eventType !== router.query.eventType &&
      !!this.state.payload?.pitchTypeId) {
      this.resetPitchTypeId();
    }

    if (router.query.componentId) {
      this.checkEventType();
    }

    if (!isEqual(prevProps.booking, booking)) {
      this.generatePayloadFromQuoteAndBooking(this.props);
    }
  }

  /**
   * When the user is amending, they cannot update whether they are booking
   * as a member or not. We default to showing member prices for the main
   * journey, but if a non-member makes a booking, we don't want to show
   * member pricing when they amend. This helper checks the user's booking
   * and if it has a non-member supplement we switch the pricing back to
   * non-member
   */
  checkAmendsMembershipSelection = () => {
    const {
      booking,
      data: {
        configuration,
      },
      router: {
        query: {
          componentId,
        },
      },
    } = this.props;
    // If we're not amending, return
    if (!booking) return;
    const siteBooking = booking.campsiteBookings.find(
      (campsiteBooking) => campsiteBooking.id === componentId,
    );
    if (!siteBooking || !siteBooking?.pitches?.length) return;
    const nmsName = configuration?.nonMemberSupplementExtraName ?? '';
    // Bookings can only ever have one pitch
    const { supplements = [] } = siteBooking.pitches[0] ?? {};
    // We then need to check if we have a non-member supplement present
    // if so, we set the memberPrices boolean to false so the user is
    // shown non-member pricing
    const hasNMS = supplements.find((supplement) => supplement.name === nmsName);
    if (hasNMS) {
      this.handleMemberNonMemberChange(false);
    }
  }

  generatePayloadFromQuoteAndBooking = (props) => {
    const {
      booking,
      router: {
        query: {
          bookingId,
          componentId,
          siteCode,
          isOverseas,
          campsiteId,
          eventType,
          start,
          end,
        },
      },
      query,
      quote,
      data: {
        configuration,
      },
      membershipStatus,
    } = props;

    const q = {
      ...query,
      start: query.start ?? start,
      end: query.end ?? end,
      campsiteId: query.campsiteId ?? campsiteId,
      componentId: query.componentId ?? componentId,
      siteCode: query.siteCode ?? siteCode,
      eventType,
      bookingId,
    };

    let membershipType = getENumWithValue(quoteMembershipTypes.NotLoggedInOrNonMember);

    if (isLoggedIn() &&
      (
        isLoggedInMember(membershipStatus?.user) ||
        RENEWAL_STATUSES.includes(membershipStatus?.user?.membershipStatus)
      )) {
      membershipType = getENumWithValue(quoteMembershipTypes.LoggedInMember);
    }

    // payload: current state of the form(api payload) being edited
    // payloads: state storage for payloads pending to be sent
    const payload = getPayloadFromQuoteAndQuery(
      q, quote, membershipType, configuration, isOverseas === 'true',
      componentId, booking,
    );

    this.setState({
      payload,
      payloads: {
        touring: payload,
      },
      pitchName: payload.pitchName || null,
      startOfCalendarMonth: payload.start || format(new Date(), 'YYYY-MM-DD'),
      isLoadingPayload: false,
    });
  }

  checkEventType = () => {
    const {
      quote, router, result,
    } = this.props;
    const { eventChecked } = this.state;
    const { componentId } = router.query ?? {};
    if ((quote?.quoteId || quote?.bookingReference) && result?.pitches?.length && !eventChecked) {
      this.setState({
        eventChecked: true,
      });
      const booking = quote.campsiteBookings.find(
        campsiteBooking => campsiteBooking.id?.includes(componentId),
      );
      if (!booking) {
        return;
      }
      const pitch = booking?.pitches.find(pitchItem => pitchItem.id === componentId);
      if (pitch) {
        const campsitePitch = result.pitches.find(pitchItem => pitchItem.id === pitch.code);
        if (campsitePitch && campsitePitch.eventType !== Number(router.query.eventType)) {
          updateRouterQuery(routes.sites, {
            eventType: campsitePitch.eventType,
          });
        }
      }
    }
  }

  resetPitchTypeId = () => {
    this.setState({
      payload: {
        ...this.state.payload,
        pitchTypeId: undefined,
      },
    });
  }

  setPayload = (datesChanged, componentIdChanged, quoteDeleted) => {
    const {
      router: {
        query: {
          componentId,
          siteCode,
          isOverseas,
          campsiteId,
          eventType,
          bookingId,
        },
      },
      query,
      quote,
      data: {
        configuration,
      },
      booking,
    } = this.props;

    const { payload } = this.state;

    if (!payload) return;

    const q = {
      ...query,
      campsiteId: query.campsiteId || campsiteId,
      componentId: query.componentId || componentId,
      siteCode: query.siteCode || siteCode,
      eventType,
      bookingId,
    };

    const membershipType = this.getMembershipType();
    const generatedPayload = getPayloadFromQuoteAndQuery(
      q, quote, membershipType, configuration, isOverseas === 'true',
      this.props.router.query?.componentId, booking,
    );

    const newPayload = {
      ...generatedPayload,
      ...payload,
      // Reset payload when editing a different campsite or quote is cleared
      ...((componentIdChanged || quoteDeleted) ? generatedPayload : {}),
      arrivalTime: payload.arrivalTime || generatedPayload.arrivalTime,
      membershipType,
      start: query.start || payload.start,
      end: query.end || payload.end,
    };
    if (datesChanged && !componentIdChanged) {
      newPayload.guests = [];
      newPayload.pitchTypeId = payload.pitchTypeId;
    }
    const newPayloads = this.getPayloads(payload);
    this.setState({
      payload: newPayload,
      payloads: newPayloads,
    });
  }

  getMembershipType = () => {
    const { user } = this.props.membershipStatus ?? {};
    if (isLoggedIn() && (isLoggedInMember(user) ||
      RENEWAL_STATUSES.includes(user?.membershipStatus))) {
      return getENumWithValue(quoteMembershipTypes.LoggedInMember);
    }
    const { membershipType } = this.props.quote ?? {};
    if (newMemberTypes.includes(membershipType)) {
      return membershipType;
    }

    return getENumWithValue(quoteMembershipTypes.NotLoggedInOrNonMember);
  }

  /**
   * @summary Fetch total price and create quote
   * @arg {Object[]} availability The AvailabilitySearch response (costPerNight: false)
   */
  handleContinueClick = (value) => {
    const continued = typeof (value) === 'undefined' ? !this.state.continued : value;
    this.setState({ continued, disabledForms: true }, () => {
      startTiming(types.AVAILABILITY_DETAILS_FOOTER);
    });
  }

  handleFormState = (disabledForms) => {
    // if (this.state.disabledForms === disabledForms) return;
    this.setState({ disabledForms });
  }

  getPayloads = (payload) => {
    const payloads = ({ ...this.state.payloads, touring: payload });
    return payloads;
  }

  updatePayload = (payload, cb) => {
    const payloads = this.getPayloads(payload);
    this.setState({ continued: false, payload, payloads }, cb);
  }

  // essentially same as above but we don't want to reset the continued state
  updatePayloads = (payload) => {
    const payloads = this.getPayloads(payload);
    this.setState({ payload, payloads });
  }

  cleanErrors = () => this.setState({ errors: [] });

  handleChange = (state, cb = () => null) => {
    const payloads = this.getPayloads(state.payload || this.state.payload);
    const newState = { ...this.state, ...state };
    newState.payloads = payloads;
    this.setState({
      ...newState,
      bookingEdited: true,
    }, () => cb(state));
  };

  handleMemberNonMemberChange = (memberPricesAreSelected) => {
    this.setState({
      memberPricesAreSelected: memberPricesAreSelected || !this.state.memberPricesAreSelected,
    }, () => {
      this.setPayload();
    });
  };

  handleCloseDialog = () => {
    this.setState({
      membershipDialog: null,
    });
  }

  handleUpdateQuoteMembershipType = async (membershipType) => {
    const { data, result } = this.props;
    const product = getProductWithCampsiteType(
      result?.type, data?.configuration.products,
    );
    const productCode = product?.productCode;
    const payload = {
      membershipType,
      productCode,
    };

    this.handleFormState(true);

    await this.props.updateQuote(payload)
      .then(this.handleBasketCallback)
      .catch(error => console.error(error))
      .finally(() => this.handleFormState(false));
  };

  handleUpdateQuote = async (newPayload) => {
    const { quote, data, result } = this.props;
    const product = getProductWithCampsiteType(
      result?.type, data?.configuration.products,
    );
    const productCode = product?.productCode;
    const payload = {
      ...newPayload,
      productCode,
      errataAccepted: true,
    };

    delete payload.specReq; // cleaning request
    this.handleFormState(true);

    if (
      quote.campsiteBookings &&
      quote.crossingBookings &&
      !quote.campsiteBookings.length &&
      !quote.crossingBookings.length &&
      quote.partyMembers.length === 1) {
      payload.partyMembers[0].personId = quote.partyMembers[0]?.personId;
    }

    await this.props.updateQuote(payload)
      .then(this.handleBasketCallback)
      .catch(error => console.error(error))
      .finally(() => this.handleFormState(false));
  };

  handleBasketCallback = () => {
    const { addToBasketCallback } = this.state;
    const pendingLogin = localStorage.pendingLogin === 'true';
    if (addToBasketCallback && !pendingLogin) {
      const basketCallback = addToBasketCallback;
      this.setState({
        addToBasketCallback: null,
        membershipDialog: null,
      }, () => {
        basketCallback();
      });
    }
    if (pendingLogin) {
      this.redirectToLogin();
    }
  }

  redirectToLogin = () => {
    redirectViaRouterToLoginWithBasketOpen();
  }

  handleLogin = (addToBasket) => {
    // indicator of pending campsite update
    localStorage.pendingCampsiteAdd = this.props.result?.name;
    this.setState({
      addToBasketCallback: null,
    }, async () => {
      if (addToBasket) {
        localStorage.pendingLogin = 'true';
        await addToBasket();
      }
      this.redirectToLogin();
    });
  }

  handleCheckMembership = (callback, newQuote, payload) => {
    const {
      data,
      result,
      quote,
      membershipStatus,
    } = this.props;
    const user = membershipStatus?.user;
    const { addToBasketCallback, membershipDialog, memberPricesAreSelected } = this.state;
    const dialogType =
      getDialogType(
        { ...newQuote, ...quote },
        result,
        memberPricesAreSelected,
        user,
        { ...payload, ...this.state.payload },
        data?.configuration,
        this.state.payloads,
      );
    const amount = newQuote?.nonMemberSupplementsCost ?? quote?.nonMemberSupplementsCost;
    const membershipCost = data?.configuration?.membershipTypes?.directDebit?.costSingle;

    if (payload) {
      if (dialogType === DIALOG_MEMBERSHIP_TYPE.MEMBERSHIP_REQUIRED && !membershipDialog) {
        const newPayload = {
          ...payload,
          membershipType: getENumWithValue(quoteMembershipTypes.MembershipByDD),
        };
        const primaryAction = async () => {
          await callback(newPayload);
          localStorage.pendingCampsiteAdd = this.props.result?.name;
          this.handleCloseDialog();
        };
        this.setState({
          membershipDialog: {
            type: dialogType,
            primaryAction,
            secondaryAction: this.handleCloseDialog,
            handleLogin: () => this.handleLogin(primaryAction),
            amount: membershipCost,
          },
        });
      } else {
        if (!dialogType) {
          localStorage.pendingCampsiteAdd = this.props.result?.name;
        }
        callback(payload);
      }
    } else if (!newQuote && callback) {
      if (!dialogType) {
        localStorage.pendingCampsiteAdd = this.props.result?.name;
      }
      this.setState({
        addToBasketCallback: callback,
      });
    } else if (!dialogType && addToBasketCallback) {
      this.handleBasketCallback();
    } else if (dialogType && addToBasketCallback && !membershipDialog) {
      const directDebitMembershipType = getENumWithValue(quoteMembershipTypes.MembershipByDD);

      if (dialogType === DIALOG_MEMBERSHIP_TYPE.MEMBERSHIP_SAVE) {
        this.setState({
          membershipDialog: {
            type: dialogType,
            primaryAction: () => {
              this.handleUpdateQuoteMembershipType(directDebitMembershipType);
              this.handleCloseDialog();
            },
            secondaryAction: this.handleBasketCallback,
            handleLogin: () => this.handleLogin(),
            amount,
            amountSecondary: membershipCost,
          },
        });
      }
      if (dialogType === DIALOG_MEMBERSHIP_TYPE.MEMBERSHIP_AUTO) {
        this.setState({
          membershipDialog: {
            type: dialogType,
            primaryAction: () => {
              this.handleUpdateQuoteMembershipType(directDebitMembershipType);
              this.handleCloseDialog();
            },
            handleLogin: () => this.handleLogin(),
            amount,
            amountSecondary: membershipCost,
          },
        });
      }
    }
    return dialogType;
  }

  handleAddToBasketClickCallBack = () => {
    const {
      router, client, result, toggleBasket,
    } = this.props;
    addToBasketRedirect(client, router.query, result?.name, toggleBasket);
  };

  handleAddToBasketClick = () => {
    const { client } = this.props;
    this.handleCheckMembership(() => this.handleAddToBasketClickCallBack(client));
  };

  handleSelectEvent = (selectedEvent) => {
    const newPayload = {
      ...this.state.payload,
      eventType: selectedEvent?.eventType,
    };
    const payloads = this.getPayloads(newPayload);
    this.setState({
      payload: newPayload,
      payloads,
    });
    this.props.onSelectEvent(selectedEvent);
  };

  render() {
    const {
      booking,
      countBookings,
      onlyExtras,
      onResultChange,
      quote,
      result,
      router: {
        query: {
          campsiteId, siteCode, bookingId,
        },
      },
      toggleBasket,
      data,
      onProductTypeMismatch,
    } = this.props;
    const visible = !!siteCode;

    const {
      bookingEdited,
      continued,
      errors,
      maxOccupancy,
      payload,
      pitchName,
      memberPricesAreSelected,
      membershipDialog,
      disabledForms,
      isLoadingPayload,
    } = this.state;

    if (data.loading || this.props.loading || !result || isLoadingPayload) {
      return <LoadingSpinner marginTop />;
    }
    const product = getProductWithCampsiteType(result?.type, data?.configuration.products);
    const dialogMembershipProps =
      getMembershipDialogProps({ dialogType: membershipDialog?.type });

    let AvailabilityComponent = Availability;
    if (bookingId) {
      AvailabilityComponent = AvailabilityAmend;
    }

    const parsedPayload = {
      ...parsePayload(this.state.payloads?.touring, null, quote?.isBookingAmendmentQuote),
      errataAccepted: true,
    };

    const bookings = booking?.campsiteBookings;
    const existingBooking = bookings?.find(({ id }) => id === payload?.id) || bookings?.[0] || {};
    const bookedPitches = existingBooking?.pitches ?? [];

    return (
      <>
        <div ref={this.topRef} />
        <MembershipDialog
          open={!!membershipDialog}
          loading={disabledForms || this.props.loading}
          campsiteId={campsiteId}
          siteCode={siteCode}
          {...dialogMembershipProps}
          {...membershipDialog}
        />

        <AddToBasket
          size="large"
          align="center"
          onCompleted={() => this.handleFormState(false)}
          onSubmit={() => this.handleFormState(true)}
          onSuccess={() => this.handleAddToBasketClick()}
          handleCheckMembership={this.handleCheckMembership}
          payload={parsedPayload}
          hidden
        />

        {!bookingId && (
          <AvailabilitySection>
            <Text dictionary={dictionaryItem('QuoteCampsite', 'Header')} />
          </AvailabilitySection>
        )}

        <AvailabilitySearchQuery
          onCompleted={({ offers }) => onResultChange({ ...result, offers })}
          booking={booking}
          isAmend={!!bookingId}
          variables={{
            calendar: {
              start: this.state.startOfCalendarMonth,
            },
            partyMembers: payload.partyMembers,
            payload: {
              start: payload.start,
              end: payload.end,
            },
            siteCode,
            productCode: product?.productCode,
            placeType: result?.type,
            pitchId: bookingId && payload?.id ? payload.id : undefined,
            pitchTypeId: payload.pitchTypeId,
          }}
          selectedEvent={this.props.selectedEvent}
          availabilitySearchQueryRef={this.availabilitySearchQueryRef}
        >
          {(availabilityProps) => {
            const {
              availabilitySearch,
              availabilitySearchLoading,
              availabilityTotal,
              fetchAvailabilitySearch,
              fetchAvailabilityTotal,
              inputDateError,
              loading,
            } = availabilityProps;
            const isSelectedEventClosed = checkIfEventIsClosedOnWithinDateRange(
              this.props.selectedEvent,
              payload.start,
              payload.end,
            );
            let closedDateError;
            if (isSelectedEventClosed) {
              closedDateError = new Error();
              closedDateError.message = dictionaryItem('AvailabilityCalendar', 'Date', 'Closed', 'Error');
            }

            // If we have results on availabilityTotal use that, fallback to availabilitySearch
            const availability = availabilityTotal?.data?.availability?.pitchTypes?.length > 0
              ? availabilityTotal
              : availabilitySearch;
            const basePrice =
              getTotalCost(availability, payload.pitchTypeId, memberPricesAreSelected, !!bookingId);

            const priceIndex = payload.pitchTypeId;

            if (basePrice && this.basePrices[priceIndex] !== basePrice) {
              const updatedBasePrices = {
                ...this.basePrices,
                [priceIndex]: basePrice,
              };
              this.basePrices = updatedBasePrices;
            }

            const isLoading = this.props.quoteLoading;
            const error = this.props.error || availabilitySearch.error || availabilityTotal.error;

            const availabilityParams = {
              availabilitySearch,
              availabilitySearchLoading,
              availabilityTotal,
              campsiteId,
              campsiteAddress: result.address,
              campsiteName: result.name,
              campsiteOpenDates: result.openDates,
              cleanErrors: this.cleanErrors,
              closedDateError,
              continued,
              disabledForms: this.state.disabledForms,
              error,
              errors,
              events: result.events,
              fetchAvailabilitySearch,
              fetchAvailabilityTotal,
              handleChange: this.handleChange,
              hasBookings: !!countBookings,
              inputDateError,
              loadingAvailability: loading,
              onlyExtras,
              onContinueClick: this.handleContinueClick,
              onQueryChange: this.props.onQueryChange,
              onSelectEvent: this.handleSelectEvent,
              partyMembers: quote ? quote.partyMembers : [],
              payload,
              pitchName,
              query: this.props.query,
              updatePayload: this.updatePayload,
              handleMemberNonMemberChange: this.handleMemberNonMemberChange,
              memberPricesAreSelected,
              type: result.type,
              visible,
              selectedEvent: this.props.selectedEvent,
              product,
              quote,
              handleUpdateQuote: this.handleUpdateQuote,
            };

            if (isLoading) {
              return (
                <Body>
                  <LoadingSpinner marginTop />
                </Body>
              );
            }

            return (
              <Fragment>
                <AvailabilityComponent
                  {...availabilityParams}
                />

                {continued &&
                  <Query
                    onError={() => this.setState({ continued: false })}
                    query={GET_AVAILABILITY_DETAILS}
                    fetchPolicy={FetchPolicy.NETWORK_ONLY}
                    variables={parseVariables({
                      end: payload.end,
                      partyMembers: payload.partyMembers,
                      crossingBookings: payload.crossingBookings,
                      pitchTypeId: payload.pitchTypeId,
                      start: payload.start,
                      siteCode: payload.siteCode,
                      useMemberPricing: memberPricesAreSelected,
                      pitchId: bookingId && payload?.id ? payload.id : undefined,
                      productCode: product?.productCode,
                    }, 'Reservations/AvailabilityDetails')}
                    // TEMP Performance Hooks, TODO can be safely removed at any time
                    onCompleted={() => {
                      addTiming(types.AVAILABILITY_DETAILS_FOOTER, 'Load Complete', true);
                    }}
                  >
                    {response => (
                      <AvailabilityDetails
                        {...response}
                        configuration={data?.configuration}
                        basePrice={basePrice}
                        basePrices={this.basePrices}
                        booking={this.props.booking}
                        bookingEdited={bookingEdited}
                        campsiteAddress={result.address}
                        campsiteId={campsiteId}
                        campsiteName={result.name}
                        componentId={this.props.router.query?.componentId}
                        disabledForms={this.state.disabledForms}
                        handleQuoteChange={this.handleChange}
                        handleCheckMembership={this.handleCheckMembership}
                        maxOccupancy={maxOccupancy}
                        memberPricesAreSelected={memberPricesAreSelected}
                        onlyExtras={onlyExtras}
                        onCompleted={this.handleFormState}
                        onSubmit={this.handleFormState}
                        payload={payload}
                        pitchName={pitchName}
                        pitches={result?.pitches}
                        product={product}
                        updatePayloads={this.updatePayloads}
                        quote={quote}
                        quoteCount={countBookings}
                        toggleBasket={toggleBasket}
                        showCancellation={bookingReducedNights(payload, bookedPitches)}
                        onProductTypeMismatch={onProductTypeMismatch}
                        searchPitches={availability?.data?.availability?.pitchTypes ?? []}
                        bookedPitches={bookedPitches}
                      />
                    )}
                  </Query>
                }
              </Fragment>
            );
          }}
        </AvailabilitySearchQuery>
      </>
    );
  }
}

export default compose(
  withRouter,
  withApollo,
  graphql(GET_CONFIGURATION),
  graphql(GET_SITES_CONFIG, {
    props: ({ data }) => ({
      sitesConfig: data.configurationSites,
    }),
  }),
  graphql(GET_MEMBER_STATUS, {
    name: 'membershipStatus',
    options: {
      fetchPolicy: FetchPolicy.CACHE_ONLY,
    },
    skip: props => !isLoggedIn(),
  }),
  graphql(GET_BOOKING_DETAILS, {
    options: ({ router }) => ({
      fetchPolicy: 'cache-and-network',
      variables: {
        bookingReference: router.query.bookingId,
        skip: !router.query.bookingId,
      },
    }),
    props: (props) => {
      const booking = getBookingFromData(props.data);
      const quote = populateQuoteFromBooking(props.ownProps.quote, booking);
      return {
        booking,
        quote,
      };
    },
  }),
  graphql(UPDATE_QUOTE, {
    props: ({ mutate }) => ({
      options: {
        notifyOnNetworkStatusChange: true,
      },
      updateQuote: input => mutate({
        update: updateQuoteCache,
        variables: { input },
        refetchQueries: ['Quote'],
      }),
    }),
  }),
  withStateMutation({ name: 'updateQuote' }),
)(QuoteCampsite);
